import { useEffect, useState } from "react";
import { ArrowLeft } from "@phosphor-icons/react";
import { closestCenter, DndContext } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { CourseDetailsEditor } from "@/components/CourseDetailsEditor";
import { CourseModuleEditor } from "@/components/CourseModuleEditor";
import { CourseResourceEditor } from "@/components/CourseResourceEditor";
import { LoadingState } from "@/components/DashboardParts";
import RoleLayout from "@/components/RoleLayout";
import { SortableEditorItem } from "@/components/SortableEditorItem";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";

export default function AdminCourseEditor() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const refresh = () => api.get(`/lms/courses/${courseId}`).then((response) => setCourse(response.data));
  const persistOrder = async (endpoint, ids, message) => {
    try {
      await api.put(endpoint, { ids });
      toast.success(message);
      refresh();
    } catch (error) { toast.error(formatApiError(error)); }
  };
  const reorderModules = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = course.modules.map((module) => module.id);
    const orderedIds = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    persistOrder(`/lms/courses/${course.id}/module-order`, orderedIds, "Modules reordered");
  };
  const reorderLessons = (moduleId, event) => {
    if (!event.over || event.active.id === event.over.id) return;
    const module = course.modules.find((item) => item.id === moduleId);
    const ids = module.lessons.map((lesson) => lesson.id);
    const orderedIds = arrayMove(ids, ids.indexOf(event.active.id), ids.indexOf(event.over.id));
    persistOrder(`/lms/courses/${course.id}/modules/${moduleId}/lesson-order`, orderedIds, "Lessons reordered");
  };
  const reorderResources = (event) => {
    if (!event.over || event.active.id === event.over.id) return;
    const ids = (course.learning_resources || []).map((resource) => resource.id);
    const orderedIds = arrayMove(ids, ids.indexOf(event.active.id), ids.indexOf(event.over.id));
    persistOrder(`/lms/courses/${course.id}/resource-order`, orderedIds, "Resources reordered");
  };

  useEffect(() => { refresh(); }, [courseId]);

  if (!course) return <LoadingState label="Opening course editor" />;

  return <RoleLayout eyebrow="Administrator workspace" title="Course editor"><Link className="back-to-login-link" to="/admin/dashboard" data-testid="back-to-admin-dashboard-link"><ArrowLeft aria-hidden="true" size={18} />Back to training operations</Link><CourseDetailsEditor course={course} onChanged={refresh} /><section className="editor-section" data-testid="course-modules-editor"><div className="editor-section-heading"><p className="eyebrow">Curriculum</p><h2>Modules and lessons</h2></div><DndContext collisionDetection={closestCenter} onDragEnd={reorderModules}><SortableContext items={course.modules.map((module) => module.id)} strategy={verticalListSortingStrategy}>{course.modules.map((module) => <SortableEditorItem id={module.id} key={module.id} testPrefix="drag-module"><CourseModuleEditor courseId={course.id} module={module} onChanged={refresh} onReorder={(event) => reorderLessons(module.id, event)} /></SortableEditorItem>)}</SortableContext></DndContext></section><CourseResourceEditor courseId={course.id} resources={course.learning_resources || []} onChanged={refresh} onReorder={reorderResources} /></RoleLayout>;
}